import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, Trash2, Send, X, Image, BarChart3, Search,
  ThumbsUp, MessageCircle, MoreHorizontal, Heart, Smile, Loader2
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Media Item Component with loading state
function MediaItem({ mediaUrl, mediaTipo, API_URL, onImageClick }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  return (
    <div className="aspect-video bg-muted relative overflow-hidden">
      {/* Loading indicator */}
      {isLoading && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}
      
      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground">
          <Image className="w-8 h-8 opacity-50" />
        </div>
      )}
      
      {mediaTipo.startsWith('video/') ? (
        <video
          src={`${API_URL}${mediaUrl}`}
          controls
          className={`w-full h-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          onLoadedData={() => setIsLoading(false)}
          onError={() => { setIsLoading(false); setHasError(true); }}
        />
      ) : (
        <img
          src={`${API_URL}${mediaUrl}`}
          alt=""
          className={`w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          onClick={onImageClick}
          onLoad={() => setIsLoading(false)}
          onError={() => { setIsLoading(false); setHasError(true); }}
        />
      )}
    </div>
  );
}

// Reaction types with emojis
const REACTIONS = {
  like: { emoji: '👍', label: 'Mi piace' },
  love: { emoji: '❤️', label: 'Adoro' },
  laugh: { emoji: '😂', label: 'Ahah' },
  wow: { emoji: '😮', label: 'Wow' },
  sad: { emoji: '😢', label: 'Triste' },
  angry: { emoji: '😡', label: 'Arrabbiato' }
};

function Community() {
  const [socialFeed, setSocialFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [showSocialPostModal, setShowSocialPostModal] = useState(false);
  const [selectedSocialPost, setSelectedSocialPost] = useState(null);
  const [socialComments, setSocialComments] = useState([]);
  const [newSocialComment, setNewSocialComment] = useState('');

  useEffect(() => {
    fetchCurrentUser();
    fetchSocialFeed();
  }, []);

  const fetchCurrentUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setCurrentUser(await res.json());
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  const fetchSocialFeed = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/social/feed`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSocialFeed(await res.json());
      }
    } catch (err) {
      console.error('Error fetching social feed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialReact = async (postId, emoji) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/social/posts/${postId}/react?emoji=${emoji}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSocialFeed();
    } catch (err) {
      console.error('Error reacting:', err);
    }
  };

  const handleSocialComment = async (postId) => {
    if (!newSocialComment.trim()) return;
    
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/social/posts/${postId}/comments?contenuto=${encodeURIComponent(newSocialComment)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNewSocialComment('');
      // Refresh comments
      const res = await fetch(`${API_URL}/api/social/posts/${postId}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSocialComments(await res.json());
      }
    } catch (err) {
      console.error('Error commenting:', err);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo post?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/social/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSocialFeed();
    } catch (err) {
      console.error('Error deleting post:', err);
    }
  };

  const handleVote = async (postId, optionIndex) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/social/posts/${postId}/vote?option_index=${optionIndex}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSocialFeed();
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  const openComments = async (post) => {
    setSelectedSocialPost(post);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/social/posts/${post._id}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSocialComments(await res.json());
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  return (
    <div className="space-y-6" data-testid="community-page">
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Community</h1>
            <p className="text-muted-foreground">Condividi con i tuoi colleghi</p>
          </div>
          <button
            onClick={() => setShowSocialPostModal(true)}
            className="btn btn-primary"
            data-testid="create-post-button"
          >
            <Plus size={18} />
            Nuovo Post
          </button>
        </div>

        {/* Create Post Area */}
        <div 
          className="p-4 bg-muted/50 rounded-lg mb-6 cursor-pointer hover:bg-muted transition-colors"
          onClick={() => setShowSocialPostModal(true)}
        >
          <div className="flex items-center gap-3">
            {currentUser?.employee?.foto_url ? (
              <img
                src={`${API_URL}${currentUser.employee.foto_url}`}
                alt=""
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-semibold">
                {currentUser?.nome?.charAt(0)}{currentUser?.cognome?.charAt(0)}
              </div>
            )}
            <div className="flex-1 px-4 py-2 bg-white rounded-full text-muted-foreground">
              Cosa stai pensando?
            </div>
          </div>
          <div className="flex gap-2 mt-3 pt-3 border-t border-border">
            <button className="flex-1 py-2 hover:bg-white rounded-lg flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Image size={18} className="text-green-500" />
              Foto/Video
            </button>
            <button className="flex-1 py-2 hover:bg-white rounded-lg flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <BarChart3 size={18} className="text-orange-500" />
              Sondaggio
            </button>
          </div>
        </div>

        {/* Social Feed */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
        ) : socialFeed.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
            <p>Nessun post ancora. Sii il primo a condividere!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {socialFeed.map((post) => (
              <SocialPostCard
                key={post._id}
                post={post}
                currentUser={currentUser}
                onReact={handleSocialReact}
                onComment={openComments}
                onDelete={handleDeletePost}
                onVote={handleVote}
                API_URL={API_URL}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Post Modal */}
      {showSocialPostModal && (
        <CreateSocialPostModal
          onClose={() => setShowSocialPostModal(false)}
          onSuccess={() => {
            setShowSocialPostModal(false);
            fetchSocialFeed();
          }}
          API_URL={API_URL}
        />
      )}

      {/* Comments Modal */}
      {selectedSocialPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold">Commenti</h3>
              <button onClick={() => setSelectedSocialPost(null)} className="p-1 hover:bg-muted rounded">
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {socialComments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nessun commento ancora</p>
              ) : (
                socialComments.map((comment) => (
                  <div key={comment._id} className="flex gap-3">
                    {comment.author_foto ? (
                      <img
                        src={`${API_URL}${comment.author_foto}`}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm flex-shrink-0">
                        {comment.author_nome?.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 bg-muted/50 rounded-lg p-3">
                      <p className="font-semibold text-sm">{comment.author_nome}</p>
                      <p className="text-sm">{comment.contenuto}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: it })}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="p-4 border-t flex gap-2">
              <input
                type="text"
                placeholder="Scrivi un commento..."
                value={newSocialComment}
                onChange={(e) => setNewSocialComment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSocialComment(selectedSocialPost._id)}
                className="flex-1 px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <button
                onClick={() => handleSocialComment(selectedSocialPost._id)}
                className="btn btn-primary rounded-full p-2"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Social Post Card Component
function SocialPostCard({ post, currentUser, onReact, onComment, onDelete, onVote, API_URL }) {
  const [showReactions, setShowReactions] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const isOwner = currentUser && post.author_id === currentUser._id;

  // Render tagged users
  const renderContentWithTags = (content, taggedUsers) => {
    if (!taggedUsers || taggedUsers.length === 0) return content;
    
    let result = content;
    taggedUsers.forEach((user) => {
      const regex = new RegExp(`@${user.nome}\\s+${user.cognome}`, 'gi');
      result = result.replace(
        regex,
        `<span class="text-primary font-semibold">@${user.nome} ${user.cognome}</span>`
      );
    });
    return <span dangerouslySetInnerHTML={{ __html: result }} />;
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="social-post-card">
      {/* Post Header */}
      <div className="p-4 flex items-center gap-3">
        {post.author_foto ? (
          <img
            src={`${API_URL}${post.author_foto}`}
            alt=""
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-semibold">
            {post.author_nome?.charAt(0)}
          </div>
        )}
        <div className="flex-1">
          <h4 className="font-semibold">{post.author_nome}</h4>
          <p className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: it })}
          </p>
        </div>
        {isOwner && (
          <button
            onClick={() => onDelete(post._id)}
            className="p-2 hover:bg-muted rounded-full text-muted-foreground hover:text-red-500"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {/* Post Content */}
      <div className="px-4 pb-3">
        <p className="whitespace-pre-wrap">
          {renderContentWithTags(post.contenuto, post.tagged_users)}
        </p>
        
        {/* Hashtags */}
        {post.hashtags && post.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {post.hashtags.map((tag, i) => (
              <span key={i} className="text-primary text-sm">#{tag}</span>
            ))}
          </div>
        )}
      </div>

      {/* Media */}
      {post.media_urls && post.media_urls.length > 0 && (
        <div className={`grid ${post.media_urls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-1`}>
          {post.media_urls.map((media, i) => {
            // Handle both object format {url, tipo} and string format
            const mediaUrl = typeof media === 'string' ? media : media.url;
            const mediaTipo = typeof media === 'string' ? 'image/jpeg' : (media.tipo || 'image/jpeg');
            
            return (
              <MediaItem 
                key={i}
                mediaUrl={mediaUrl}
                mediaTipo={mediaTipo}
                API_URL={API_URL}
                onImageClick={() => setLightboxImage(`${API_URL}${mediaUrl}`)}
              />
            );
          })}
        </div>
      )}
      
      {/* Lightbox for full-size image */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            onClick={() => setLightboxImage(null)}
          >
            <X size={32} />
          </button>
          <img
            src={lightboxImage}
            alt=""
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Poll */}
      {post.tipo === 'sondaggio' && post.poll && (
        <div className="px-4 py-3 space-y-2">
          <p className="font-semibold">{post.poll.domanda}</p>
          {post.poll.opzioni.map((opt, i) => {
            const totalVotes = post.poll.opzioni.reduce((sum, o) => sum + (o.voti || 0), 0);
            const percentage = totalVotes > 0 ? Math.round((opt.voti || 0) / totalVotes * 100) : 0;
            const hasVoted = opt.votanti?.includes(currentUser?._id);
            
            return (
              <button
                key={i}
                onClick={() => onVote(post._id, i)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  hasVoted ? 'border-primary bg-primary/10' : 'hover:bg-muted'
                }`}
                disabled={hasVoted}
              >
                <div className="flex justify-between items-center">
                  <span>{opt.testo}</span>
                  <span className="text-sm text-muted-foreground">{percentage}%</span>
                </div>
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </button>
            );
          })}
          <p className="text-xs text-muted-foreground text-center mt-2">
            {post.poll.opzioni.reduce((sum, o) => sum + (o.voti || 0), 0)} voti
          </p>
        </div>
      )}

      {/* Reactions Summary */}
      {post.reactions_count > 0 && (
        <div className="px-4 py-2 flex items-center gap-2 text-sm text-muted-foreground border-t">
          <span>👍 {post.reactions_count}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex border-t">
        <div className="flex-1 relative">
          <button
            onClick={() => setShowReactions(!showReactions)}
            onBlur={() => setTimeout(() => setShowReactions(false), 200)}
            className="w-full py-3 hover:bg-muted flex items-center justify-center gap-2 text-muted-foreground"
          >
            <ThumbsUp size={18} />
            Mi piace
          </button>
          
          {showReactions && (
            <div className="absolute bottom-full left-0 mb-2 p-2 bg-white rounded-lg shadow-lg border flex gap-1">
              {Object.entries(REACTIONS).map(([key, { emoji, label }]) => (
                <button
                  key={key}
                  onClick={() => {
                    onReact(post._id, key);
                    setShowReactions(false);
                  }}
                  className="p-2 hover:bg-muted rounded-lg text-xl"
                  title={label}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
        
        <button
          onClick={() => onComment(post)}
          className="flex-1 py-3 hover:bg-muted flex items-center justify-center gap-2 text-muted-foreground border-l"
        >
          <MessageCircle size={18} />
          Commenta
        </button>
      </div>
    </div>
  );
}

// Create Social Post Modal
function CreateSocialPostModal({ onClose, onSuccess, API_URL }) {
  const [content, setContent] = useState('');
  const [tipo, setTipo] = useState('post');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [posting, setPosting] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const handleContentChange = async (e) => {
    const value = e.target.value;
    setContent(value);
    
    // Check for @mention
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = value.slice(lastAtIndex + 1);
      const spaceAfter = textAfterAt.indexOf(' ');
      const searchTerm = spaceAfter === -1 ? textAfterAt : textAfterAt.slice(0, spaceAfter);
      
      if (searchTerm.length >= 1 && spaceAfter === -1) {
        // Search users
        const token = localStorage.getItem('token');
        try {
          const res = await fetch(`${API_URL}/api/social/search/users?q=${searchTerm}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const users = await res.json();
            setMentionSuggestions(users);
            setShowMentions(users.length > 0);
          }
        } catch (err) {
          console.error('Error searching users:', err);
        }
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (user) => {
    const lastAtIndex = content.lastIndexOf('@');
    const newContent = content.slice(0, lastAtIndex) + `@${user.nome} ${user.cognome} `;
    setContent(newContent);
    setShowMentions(false);
    textareaRef.current?.focus();
  };

  const handleSubmit = async () => {
    if (!content.trim() && mediaFiles.length === 0 && tipo !== 'sondaggio') return;
    if (tipo === 'sondaggio' && (!pollQuestion || pollOptions.filter(o => o.trim()).length < 2)) return;

    setPosting(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    
    formData.append('contenuto', content);
    formData.append('tipo', tipo);
    
    // Extract hashtags
    const hashtags = content.match(/#(\w+)/g)?.map(t => t.slice(1)) || [];
    formData.append('hashtags', JSON.stringify(hashtags));
    
    // Extract mentions
    const mentions = content.match(/@(\w+\s+\w+)/g)?.map(m => m.slice(1)) || [];
    formData.append('mentioned_names', JSON.stringify(mentions));
    
    // Add media files
    mediaFiles.forEach(file => {
      formData.append('files', file);
    });
    
    // Add poll data
    if (tipo === 'sondaggio') {
      formData.append('poll_question', pollQuestion);
      formData.append('poll_options', JSON.stringify(pollOptions.filter(o => o.trim())));
    }

    try {
      const res = await fetch(`${API_URL}/api/social/posts`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error creating post:', err);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-lg">Crea Post</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Post Type Selector */}
          <div className="flex gap-2">
            <button
              onClick={() => setTipo('post')}
              className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 ${
                tipo === 'post' ? 'bg-primary text-white' : 'bg-muted'
              }`}
            >
              <MessageCircle size={18} />
              Post
            </button>
            <button
              onClick={() => setTipo('sondaggio')}
              className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 ${
                tipo === 'sondaggio' ? 'bg-primary text-white' : 'bg-muted'
              }`}
            >
              <BarChart3 size={18} />
              Sondaggio
            </button>
          </div>

          {/* Content */}
          <div className="relative">
            <textarea
              ref={textareaRef}
              placeholder="Cosa vuoi condividere? Usa @ per menzionare colleghi e # per hashtag"
              value={content}
              onChange={handleContentChange}
              className="w-full p-4 border rounded-lg min-h-[120px] focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            />
            
            {/* Mention Suggestions */}
            {showMentions && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-lg border max-h-48 overflow-y-auto z-10">
                {mentionSuggestions.map((user) => (
                  <button
                    key={user._id}
                    onClick={() => insertMention(user)}
                    className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-3"
                  >
                    {user.foto_url ? (
                      <img
                        src={`${API_URL}${user.foto_url}`}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm">
                        {user.nome?.charAt(0)}{user.cognome?.charAt(0)}
                      </div>
                    )}
                    <span>{user.nome} {user.cognome}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Poll Options */}
          {tipo === 'sondaggio' && (
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Domanda del sondaggio"
                value={pollQuestion}
                onChange={(e) => setPollQuestion(e.target.value)}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              {pollOptions.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    placeholder={`Opzione ${i + 1}`}
                    value={opt}
                    onChange={(e) => {
                      const newOptions = [...pollOptions];
                      newOptions[i] = e.target.value;
                      setPollOptions(newOptions);
                    }}
                    className="flex-1 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  {i >= 2 && (
                    <button
                      onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}
                      className="p-3 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ))}
              {pollOptions.length < 5 && (
                <button
                  onClick={() => setPollOptions([...pollOptions, ''])}
                  className="text-primary text-sm hover:underline"
                >
                  + Aggiungi opzione
                </button>
              )}
            </div>
          )}

          {/* Media Upload */}
          {tipo === 'post' && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={(e) => setMediaFiles([...mediaFiles, ...Array.from(e.target.files)])}
              />
              
              {mediaFiles.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {mediaFiles.map((file, i) => (
                    <div key={i} className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                      {file.type.startsWith('video/') ? (
                        <video
                          src={URL.createObjectURL(file)}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img
                          src={URL.createObjectURL(file)}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      )}
                      <button
                        onClick={() => setMediaFiles(mediaFiles.filter((_, j) => j !== i))}
                        className="absolute top-2 right-2 p-1 bg-black/50 text-white rounded-full"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
              >
                <Image size={20} />
                Aggiungi foto/video
              </button>
            </>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-secondary">
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            disabled={posting || (!content.trim() && mediaFiles.length === 0 && tipo !== 'sondaggio')}
            className="btn btn-primary"
          >
            {posting ? 'Pubblicazione...' : 'Pubblica'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Community;
